/**
 * Mock metadata for the Workspace Selector.
 *
 * `POST /v0/workspace/list` doesn't yet return industry, role, studio access
 * or last-visited data (see the backend dependency on CT-4937), so this module
 * synthesizes those fields deterministically per workspace — stable across
 * renders and reloads, so the list never reshuffles under the user.
 *
 * Swapping to real data is a one-file change: replace `synthesizeWorkspaceMeta`
 * with a mapper over the API payload and delete the tables below.
 */
import { STUDIOS } from '../workspaceSelectorData.js';

const STUDIO_BY_ID = Object.fromEntries(STUDIOS.map((studio) => [studio.id, studio]));

/**
 * Studio access per role — the mock stand-in for `studio_access[]` on
 * `POST /v0/workspace/list` (CT-4941).
 *
 * A studio the role can't reach is simply absent from the card, not rendered
 * disabled: the AC is explicit that it "never renders". Order follows STUDIOS.
 */
const ROLE_STUDIO_IDS = {
  'Platform Admin': ['governance-studio', 'data-studio', 'agentic-studio', 'work-surfaces'],
  'Org Admin': ['governance-studio', 'data-studio', 'agentic-studio'],
  Member: ['data-studio', 'agentic-studio'],
  Viewer: ['data-studio'],
};

/** Least-privilege fallback: an unrecognised role gets the Viewer set. */
function studiosForRole(roleLabel) {
  const ids = ROLE_STUDIO_IDS[roleLabel] ?? ROLE_STUDIO_IDS.Viewer;
  return ids.map((id) => STUDIO_BY_ID[id]).filter(Boolean);
}

const INDUSTRIES = [
  { label: 'FinTech', variant: 'informative' },
  { label: 'Healthcare', variant: 'success' },
  { label: 'Retail', variant: 'purple' },
  { label: 'Wealth Mgmt', variant: 'yellow' },
  { label: 'Logistics', variant: 'limeGreen' },
  { label: 'Analytics', variant: 'lightBlue' },
  { label: 'LegalTech', variant: 'lightBlue' },
  { label: 'Sandbox', variant: 'secondary' },
];

const ROLES = [
  { label: 'Platform Admin', variant: 'informative' },
  { label: 'Org Admin', variant: 'success' },
  { label: 'Member', variant: 'neutral' },
  { label: 'Viewer', variant: 'neutral' },
];

/** Lookup for a role the SERVER sent, tolerant of casing and snake_case. */
const ROLE_BY_KEY = Object.fromEntries(
  ROLES.map((r) => [r.label.toLowerCase().replace(/[\s_-]+/g, ''), r]),
);

/**
 * The real membership role wins over the synthesized one whenever
 * `POST /v0/workspace/list` provides it (carried as `membershipRole` — see
 * `toTopbarTenant`, which overwrites `role` for the Topbar dropdown).
 *
 * A role label the front end doesn't know is shown verbatim with neutral
 * chrome rather than dropped; note that `studiosForRole` then falls back to
 * the Viewer studio set for it, which is deliberate least privilege.
 */
function resolveRole(membershipRole, seed) {
  if (typeof membershipRole === 'string' && membershipRole.trim()) {
    const key = membershipRole.toLowerCase().replace(/[\s_-]+/g, '');
    return ROLE_BY_KEY[key] ?? { label: membershipRole.trim(), variant: 'neutral' };
  }
  return ROLES[(seed >>> 3) % ROLES.length];
}

/**
 * Avatar accents, cycled deterministically. Each entry pairs a light-theme text
 * shade with its dark-theme counterpart so the initials keep enough contrast on
 * both `bg-surface-elevated` variants — a single fixed shade (e.g.
 * `text-sky-300`) is unreadable on the light surface.
 */
const AVATAR_ACCENTS = [
  'ring-sky-500/40 text-sky-700 dark:text-sky-300',
  'ring-emerald-500/40 text-emerald-700 dark:text-emerald-300',
  'ring-purple-500/40 text-purple-700 dark:text-purple-300',
  'ring-amber-600/40 text-amber-700 dark:text-amber-300',
  'ring-lime-600/40 text-lime-700 dark:text-lime-300',
  'ring-cyan-600/40 text-cyan-700 dark:text-cyan-300',
  'ring-rose-500/40 text-rose-700 dark:text-rose-300',
];

/** Recency buckets — lower rank = more recently visited (used by the sort). */
const RECENCY = [
  '2h ago',
  'Earlier today',
  '3 days ago',
  '1 week ago',
  '2 weeks ago',
  '3 weeks ago',
  '1 month ago',
  '6 weeks ago',
];

/** Stable non-negative hash of a string (djb2). */
function hashString(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  }
  return h;
}

/**
 * Deterministically synthesize display metadata for a workspace. The active
 * workspace is pinned to the most-recent recency bucket so the resume banner
 * and "Active workspace" state line up.
 *
 * @param {{ id: string, name: string, initials?: string, membershipRole?: string }} workspace
 * @param {{ isActive?: boolean }} [opts]
 */
export function synthesizeWorkspaceMeta(workspace, { isActive = false } = {}) {
  const seed = hashString(workspace.id || workspace.name || 'workspace');

  const industry = INDUSTRIES[seed % INDUSTRIES.length];
  const role = resolveRole(workspace.membershipRole, seed);

  // Chips are role-filtered (CT-4941), so the set comes from the role's access
  // list rather than a per-workspace random pick.
  const studios = studiosForRole(role.label);

  const recencyRank = isActive ? 0 : 1 + ((seed >>> 5) % (RECENCY.length - 1));
  // Guarded: `studios` is never empty today (the Viewer fallback always yields
  // Data Studio), but a bad id in ROLE_STUDIO_IDS would make this a modulo by
  // zero and blow up the card on `lastStudio.id`.
  const lastStudio = studios.length ? studios[(seed >>> 7) % studios.length] : null;

  return {
    industry: industry.label,
    industryVariant: industry.variant,
    role: role.label,
    roleVariant: role.variant,
    studios,
    /** Where the card itself navigates when no chip was clicked (CT-4940). */
    defaultStudioId: studios[0]?.id,
    recencyRank,
    lastVisitedLabel: RECENCY[recencyRank],
    lastVisitedStudioId: lastStudio?.id,
    avatarClassName: AVATAR_ACCENTS[seed % AVATAR_ACCENTS.length],
  };
}
